/**
 * Shows a context-sensitive header text, which depends on the currently used route.
 * E.g. if the user navigates to "/admin/dashboard", the text will automatically render "Dashboard"
 */

import React, {useContext, useEffect, useState} from 'react'
import {useStyles} from "./CurrentAppbarContent.css";
import {Divider, LinearProgress} from "@mui/material";
import {AppbarContentContext} from "./AppbarContentProvider";
import logo from 'assets/judged_quadratic_transparent.png';
import {IconButton} from "@mui/material";
import {ArrowBack, CheckCircle, Search} from "@mui/icons-material";
import BasicHint from "../../utils/hint/BasicHint";
import UploadProgressIndication from "../../elements/progress/upload/UploadProgressIndication";
import {motion} from "framer-motion";

const CurrentAppbarContent = () => {
    const {currentAppbarContent, backgroundJobs} = useContext(AppbarContentContext)
    const classes = useStyles()

    // State storing the whether the filter section should be rendered or not
    const [searchActive, setSearchActive] = useState(false)

    /*
      Each view change changes currentAppbarContent to change as well - reset the search mode as well (i.e. hide filters)
      IMPORTANT: React only on header changes (as the search container can change while the user did not change the page - e.g. when videos are refreshed)
     */
    useEffect(() => {
        setSearchActive(false)
    }, [currentAppbarContent.header])

    return (
        <div>
            <div className={classes.appbarRow}>

                <div className={classes.appbarContentRow}>
                    {/* Filter button: Button for LEAVING the search mode */}
                    {currentAppbarContent.searchContainer && searchActive &&
                    <IconButton onClick={() => setSearchActive(!searchActive)} size="large">
                        <ArrowBack className={classes.searchIcon}/>
                    </IconButton>}

                    {/* Conditionally show "filter section" OR "normal header with actions" */}
                    {searchActive
                        ? currentAppbarContent.searchContainer
                        : <div className={classes.headerContainer}>

                            {/* Logo */}
                            <div className={classes.logoContainer}>
                                <img className={classes.logo} src={logo} alt='App logo'/>
                            </div>

                            {/* Header + description */}
                            <div>
                                <div className={classes.headerText}>{currentAppbarContent.header}</div>
                                <div className={classes.descriptionText}>{currentAppbarContent.description}</div>
                            </div>

                            <Divider className={classes.divider} orientation="vertical" flexItem/>

                            {/* Actions (do not show in case the action is already in progress) */}
                            {!currentAppbarContent.progress && <div className={classes.actionsContainer}>
                                {currentAppbarContent.actions}
                            </div>}
                        </div>}

                    {/* Filter button: Button for ENTERING the search mode */}
                    {currentAppbarContent.searchContainer && !searchActive &&
                    <IconButton onClick={() => setSearchActive(!searchActive)} size="large">
                        <Search className={classes.searchIcon}/>
                    </IconButton>}
                </div>

                {/* Indication for jobs running in background (used for indicating video uploads) */}
                {!searchActive && Object.keys(backgroundJobs).length > 0 &&
                <div className={classes.jobProgressSection}>
                    <div className={classes.jobProgressRow}>
                        {Object.keys(backgroundJobs).map((jobId) => (
                            <BasicHint key={jobId}
                                       options={{trigger: "hover", placement: 'bottom'}}
                                       hintText={backgroundJobs[jobId].tooltip}
                                       element={
                                           <div className={classes.jobProgressIndication}>
                                               {backgroundJobs[jobId].progress === 100
                                                   ? <motion.div key={jobId}
                                                                 animate={{scale: [1.05, 1.1, 1.15, 1.2, 1]}}>
                                                       <CheckCircle className={classes.jobProgressCompletedIcon}/>
                                                   </motion.div>
                                                   : <UploadProgressIndication
                                                       progress={backgroundJobs[jobId].progress}/>}
                                           </div>
                                       }/>
                        ))}
                    </div>
                </div>}
            </div>

            {/* Conditionally show progress bar */}
            {currentAppbarContent.progress && <LinearProgress className={classes.progress}/>}
        </div>
    );
}
export default CurrentAppbarContent
